"use client";

import React from "react";

export interface EmotionalMirrorProps {
  analysisResult: Record<string, unknown>;
  className?: string;
}

/**
 * "Here's what we see" narrative block that reflects back the user's
 * assessment answers. Supports both custom goal-plan data (primaryGoal,
 * challenges) and standard analysis data (category, insights).
 */
export function EmotionalMirrorContent({ analysisResult, className }: EmotionalMirrorProps) {
  // Custom analysis fields (e.g., personalized-goal-plan)
  const primaryGoal = analysisResult?.primaryGoal as { focusArea?: string; title?: string } | undefined;
  const challenges = analysisResult?.challenges as Array<{ challenge?: string }> | undefined;
  // Standard analysis fields
  const category = analysisResult?.category as string | undefined;
  const insights = analysisResult?.insights as string[] | undefined;

  const hasCustomData = !!(primaryGoal?.focusArea || primaryGoal?.title || challenges?.[0]?.challenge);

  return (
    <div className={className}>
      <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-6 md:p-8 border border-emerald-100">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Argent CF, serif' }}>
          Here&apos;s what we see
        </h2>
        {hasCustomData ? (
          <>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed">
              You&apos;re someone focused on{" "}
              <strong className="text-emerald-700">{primaryGoal?.focusArea || "personal growth"}</strong> who
              wants to <strong className="text-emerald-700">{primaryGoal?.title || "achieve your goal"}</strong>.
              {challenges?.[0]?.challenge && (
                <> The biggest thing standing in your way is <strong>{challenges[0].challenge}</strong>.</>
              )}
            </p>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed mt-3">
              This isn&apos;t just another goal. <strong>This matters to you.</strong> And that&apos;s exactly why
              a personalized plan — not generic advice — is what will get you there.
            </p>
          </>
        ) : (
          <>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed">
              Based on your answers, you&apos;re a{" "}
              <strong className="text-emerald-700">{category || "unique individual"}</strong>.
              {insights?.[0] && <> {insights[0]}</>}
            </p>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed mt-3">
              This isn&apos;t just a number. <strong>Your results reflect real patterns in your life.</strong> And
              that&apos;s exactly why a personalized approach — not generic advice — is what will help you move forward.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
